import React from 'react';
import dayjs from "dayjs";
import { UserCircle, Calendar as CalendarIcon } from "lucide-react";
import { UserActions } from './api/activity_log';
  
export interface Log {
    name: string;
    actions: UserActions;
}

export const getActiveFilterLabel = (from: Date, to: Date): string => {
    const today = dayjs().startOf('day');
    const fromDay = dayjs(from).startOf('day');
    const toDay = dayjs(to).startOf('day');
    
    const diffDays = toDay.diff(fromDay, 'day') + 1;

    if (diffDays === 1 && fromDay.isSame(today)) return "Today";
    if (diffDays === 7 && fromDay.isSame(today.subtract(6, 'day'))) return "Last 7 Days";
    if (diffDays === 30 && fromDay.isSame(today.subtract(29, 'day'))) return "Last 30 Days";
    if (diffDays === 90 && fromDay.isSame(today.subtract(89, 'day'))) return "Last 90 Days";

    return "Date Range"; 
};

interface SummaryCardProps {
    mode?: "single" | "dual";
    value?: number;
    label?: string;
    color?: string;
    extra?: string;
    left?: { value: number; label: string; color?: string };
    right?: { value: number; label: string; color?: string };
}

const SummaryCard: React.FC<SummaryCardProps> = ({mode = "single", value, label, color = "text-gray-900", extra, left, right}) => {
    if (mode === "dual" && left && right) {
        return (
            <div className="bg-white border border-gray-200 rounded-xl p-4 relative">
                <div className="absolute inset-y-2 left-1/2 w-px bg-gray-300"></div>

                <div className="grid grid-cols-2">
                    <div className="pr-3">
                        <div className={`text-2xl font-bold ${left.color ?? "text-sky-600"}`}>
                            {left.value}
                        </div>
                        <div className="text-sm text-gray-600 font-medium">{left.label}</div>
                    </div>

                    <div className="pl-6">
                        <div className={`text-2xl font-bold ${right.color ?? "text-sky-600"}`}>
                            {right.value}
                        </div>
                        <div className="text-sm text-gray-600 font-medium">{right.label}</div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white border border-gray-200 rounded-xl p-4">
            <div className={`text-2xl font-bold ${color} mb-1`}>{value}</div>
            <div className="text-sm text-gray-600 font-medium flex items-center justify-between">
                {label}
                {extra && <span className="text-xs text-gray-500">{extra}</span>}
            </div>
        </div>
    );
};

interface ActivityLogCardProps {
    log: Log;
}

const ActivityLogCard: React.FC<ActivityLogCardProps> = ({ log }) => {
    const totalPings =
        log.actions.add_manual_ping + log.actions.delete_ping + log.actions.restore_ping;

    return (
        <div className="w-full bg-gray-50 rounded-2xl p-4 border border-gray-200">
            <div className="flex items-center mb-4">
                <UserCircle className="h-5 w-5 text-gray-400 mr-2" />
                <h2 className="text-sm font-semibold text-gray-900 truncate">{log.name}</h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
               <SummaryCard mode="dual" 
                    left={{value: log.actions.add_delivery_date, label: "Delivery Date Count", color: "text-sky-600"}}
                    right={{value: log.actions.set_cn_delivery_date, label: "CN Count", color: "text-sky-600"}}
                />

                <SummaryCard
                    value={totalPings}
                    label="Manual Pings"
                    color="text-purple-600"
                    extra={`Added ${log.actions.add_manual_ping}, Deleted ${
                        log.actions.delete_ping
                    }, Restored ${log.actions.restore_ping}`}
                />

                <SummaryCard
                    value={log.actions.add_geolocation}
                    label="Geolocations Added"
                    color="text-green-600"
                />
            </div>
        </div>
    );
};

interface LogSectionProps {
    logs: Log[];
    loading: boolean;
}

const LogSection: React.FC<LogSectionProps> = ({ logs, loading }) => {
    if (loading) {
        return (
            <div className="text-center text-gray-500 py-20">
                <CalendarIcon className="animate-spin inline-block h-6 w-6 mr-2" />
                Loading activity logs...
            </div>
        );
    }

    if (logs.length === 0) {
        return (
            <div className="text-center text-gray-500 py-20 text-lg border-2 border-dashed border-gray-200 rounded-xl p-10 bg-gray-50">
                No activity logs found for the selected date range.
            </div>
        );
    }

    const totalDeliveryDates = logs.reduce((sum, log) => sum + log.actions.add_delivery_date, 0);

    const totalManualPings = logs.reduce((sum, log) => sum + log.actions.add_manual_ping + log.actions.delete_ping + log.actions.restore_ping, 0);

    const totalGeoLocations = logs.reduce((sum, log) => sum + log.actions.add_geolocation, 0);

    const sortedLogs = [...logs].sort((a, b) => b.actions.add_delivery_date - a.actions.add_delivery_date);

    return (
        <div className="space-y-4">
            <div className="w-full bg-gray-100 rounded-2xl p-4 border border-gray-300 shadow-sm">
                <h2 className="text-sm font-semibold text-gray-900 flex items-center mb-4">
                    <UserCircle className="h-5 w-5 text-gray-500 mr-2" />
                    Overall Summary
                </h2>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <SummaryCard mode="dual"
                        left={{value: totalDeliveryDates, label: "Total Delivery Date Count", color: "text-sky-700"}}
                        right={{value: logs.reduce((sum, log) => sum + log.actions.set_cn_delivery_date, 0), label: "Total CN Count", color: "text-sky-700"}} 
                    />

                    <SummaryCard
                        value={totalManualPings}
                        label="Total Manual Pings"
                        color="text-purple-700"
                    />

                    <SummaryCard
                        value={totalGeoLocations}
                        label="Total Geolocations Added"
                        color="text-green-700"
                    />
                </div>
            </div>
            {sortedLogs.map((log, idx) => (
                <ActivityLogCard key={idx} log={log} />
            ))}
        </div>
    );
};

export default LogSection;